/**
 * LOGOI LAB - Layer 6: Inhibition Policy
 * π(x) — Decision policy based on Λ̇
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { InhibitionPolicy, MetaPolicy, LevinSignal } from '../types';
import { LAMBDA_STAR, V_STAR, S_STAR } from '../domain/constants';

/**
 * π(x) — Inhibition Policy based on Λ̇
 * 
 * ACTÚA when Λ̇ > 0 (approaching Logos)
 * ESPERA when Λ̇ ≈ 0 (stagnant)
 * PAUSA when Λ̇ < 0 (moving away from Logos)
 */
export function inhibitionPolicy(
  Lambda: number, 
  V: number, 
  S: number, 
  C: number, 
  prevLambda?: number
): InhibitionPolicy {
  const lambdaDot = Lambda - (prevLambda ?? Lambda);
  
  if (Lambda < 0.15) {
    return { 
      verdict: "PAUSA", 
      reasonKey: "pausaCritical",
      color: "#ef4444", 
      reason: "Λ crítico. Canal Logos prácticamente cerrado. No tomes decisiones. Primero reconecta.", 
      icon: "🛑", 
      lambdaDot 
    };
  }
  
  if (V < 0.30) {
    return { 
      verdict: "PAUSA", 
      reasonKey: "pausaViability",
      color: "#ef4444", 
      reason: "Viabilidad colapsada. Protege al decisor antes que la decisión.", 
      icon: "🛑", 
      lambdaDot 
    };
  }
  
  if (S > 0.55) {
    return { 
      verdict: "ESPERA", 
      reasonKey: "esperaEntropy",
      color: "#f59e0b", 
      reason: "Entropía alta. El ruido ahoga la señal. Estabiliza primero.", 
      icon: "⏸", 
      lambdaDot 
    };
  }
  
  if (C < 0.30) {
    return { 
      verdict: "ESPERA", 
      reasonKey: "esperaCoherence",
      color: "#f59e0b", 
      reason: "Coherencia insuficiente. Tus dimensiones no están alineadas.", 
      icon: "⏸", 
      lambdaDot 
    };
  }
  
  if (lambdaDot < -0.02) {
    return { 
      verdict: "RECONECTA", 
      reasonKey: "reconecta",
      color: "#a855f7", 
      reason: "Λ̇ < 0: Te estás alejando del Logos. Detente y reconecta antes de actuar.", 
      icon: "🔮", 
      lambdaDot 
    };
  }
  
  if (Lambda > 0.50 && V > 0.55 && S < 0.35 && C > 0.45) {
    return { 
      verdict: "ACTÚA", 
      reasonKey: "actua",
      color: "#10b981", 
      reason: "Λ̇ ≥ 0, sistema alineado. Procede con confianza — la señal es clara.", 
      icon: "✦", 
      lambdaDot 
    };
  }
  
  return { 
    verdict: "MONITOREA", 
    reasonKey: "monitorea",
    color: "#6366f1", 
    reason: "Sistema en transición. Avanza con cautela y atención plena.", 
    icon: "◎", 
    lambdaDot 
  };
}

/**
 * π²(x) — Meta-Policy: Attractor Detection
 * Includes detection of negative attractors A⁻
 */
export function metaPolicy(
  Lambda: number, 
  V: number, 
  S: number, 
  C: number, 
  signals: LevinSignal[]
): MetaPolicy {
  const hasNegativeAttractor = signals.some(sig => sig.type.includes("ATRACTOR NEGATIVO"));
  
  if (hasNegativeAttractor) {
    return { 
      verdict: "ESCAPE A⁻", 
      reasonKey: "escapeNeg",
      color: "#dc2626", 
      reason: "Atractor negativo detectado. Espiral entrópica activa. Requiere intervención disruptiva INMEDIATA." 
    };
  }
  
  if (signals.length > 0 && signals.some(s => s.severity > 0.75)) {
    return { 
      verdict: "ESCAPE", 
      reasonKey: "escape",
      color: "#f97316", 
      reason: `Patrón patológico: ${signals.map(s => s.type).join(", ")}. Protocolo de desestabilización controlada.` 
    };
  }
  
  if (Lambda < LAMBDA_STAR && V < V_STAR) {
    return { 
      verdict: "ACTIVAR", 
      reasonKey: "activar",
      color: "#eab308", 
      reason: "Sistema por debajo de A⁺. Incrementar alineación Logos: meditación, servicio, presencia." 
    };
  }
  
  if (Lambda >= LAMBDA_STAR && V >= V_STAR && S < S_STAR) {
    return { 
      verdict: "SOSTENER", 
      reasonKey: "sostener",
      color: "#10b981", 
      reason: "Sistema convergiendo a A⁺. Mantener prácticas. El Logos fluye." 
    };
  }
  
  return { 
    verdict: "EVOLUCIONAR", 
    reasonKey: "evolucionar",
    color: "#6366f1", 
    reason: "Sin patología. Busca crecimiento consciente en la dimensión que más te llame." 
  };
}
